import { Box, ClickAwayListener, Paper, Typography } from '@mui/material'
import { FC, KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GRAY_700, GRAY_900, GRAY_BORDER } from 'constants/styling/theme'
import { GooglePlaceComponent, getFromGooglePlace } from 'utils/location/googlePlaceUtils'

import { GoogleAPIPlace } from 'models/purchaseFlow'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { getCityFromGooglePlace } from 'utils/location'
import { getGoogleApiKey } from 'constants/API'
import i18n from 'translations/i18n'
import { logger } from 'utils/logging'
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

/** Postal components parsed from the selected Google place. Omitted components are empty strings. */
export interface SelectedAddress {
  /** Street name (full, never abbreviated), with the house number appended when Google provides one. */
  streetAndNumber: string
  postalCode: string
  city: string
  /** ISO country code of the place — informational, callers decide whether to apply it. */
  countryCode: string
}

interface Props {
  /** Label of the street input. */
  label?: string
  /** Placeholder hinting that typing searches addresses (only shown while autocomplete is active). */
  placeholder?: string
  /** Controlled value of the street field (owned by the surrounding form). */
  value: string
  /** Change handler of the street field (owned by the surrounding form). */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  isError?: boolean
  errorText?: string
  /** ISO country code(s) to restrict the predictions to (e.g. the account's billing country). */
  restrictToCountry?: string | string[]
  disabled?: boolean
  onBlur?: () => void
  /** Called with the typed text when Enter is pressed without a suggestion picked, for callers that geocode it themselves. */
  onSubmitText?: (text: string) => void
  /** Leading icon inside the input, e.g. a search glyph (only shown while autocomplete is active). */
  startIcon?: ReactNode
  /** Called with the parsed postal components when the user picks a prediction, and the picked place
   *  itself (formatted address, coordinates, components) for callers that need more than the postal lines. */
  onSelect: (address: SelectedAddress, place: google.maps.places.PlaceResult) => void
  /** Focuses the field and shows the suggestions for its initial value, for a value the user already typed elsewhere. */
  searchOnMount?: boolean
}

/**
 * The street input itself doubles as a Google Places search: typing edits the form's street value
 * as usual and surfaces address predictions beneath the field; picking one hands the parsed postal
 * components to the caller. The form stays the single source of truth — the component owns no
 * address state, so manual entry is simply "type and never pick".
 *
 * Degrades to the plain street input when the Google API key is not configured — a form embedding
 * this input must keep working manually rather than being Maps-gated (the key has been
 * mis-provisioned in a deployed bundle before, see constants/API/googleAPI.ts).
 */
export const AddressSearchInput: FC<Props> = (props) => {
  const apiKey = useMemo(() => {
    try {
      return getGoogleApiKey()
    } catch (e) {
      logger.error('Address search disabled: Google API key unavailable', e as Error, {
        context: { component: 'AddressSearchInput', action: 'missingApiKey' },
      })
      return null
    }
  }, [])

  if (!apiKey) {
    return (
      <MUIInputField
        label={props.label}
        value={props.value}
        onChange={props.onChange}
        required={props.required}
        isError={props.isError}
        errorText={props.errorText}
        disabled={props.disabled}
        onBlur={props.onBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') props.onSubmitText?.(props.value) }}
      />
    )
  }

  return <AddressSearchInputLoaded {...props} apiKey={apiKey} />
}

/** A suggestion as the Google dropdown shows it: the typed part bold, the rest of the street plain, the place grey. */
const PredictionLabel: FC<{ prediction: google.maps.places.AutocompletePrediction }> = ({ prediction }) => {
  const { main_text: main, main_text_matched_substrings: matches = [], secondary_text: secondary } = prediction.structured_formatting ?? {}
  if (!main) return <Typography variant="text-sm" color={GRAY_900} sx={{ whiteSpace: 'normal' }}>{prediction.description}</Typography>

  const parts: ReactNode[] = []
  let cursor = 0
  matches.forEach(({ offset, length }, index) => {
    if (offset > cursor) parts.push(main.slice(cursor, offset))
    parts.push(<strong key={index}>{main.slice(offset, offset + length)}</strong>)
    cursor = offset + length
  })
  parts.push(main.slice(cursor))

  return (
    <Typography variant="text-sm" color={GRAY_900} sx={{ whiteSpace: 'normal' }}>
      {parts}
      {secondary && <Typography component="span" variant="text-sm" color={GRAY_700}> {secondary}</Typography>}
    </Typography>
  )
}

const SEARCH_ON_MOUNT_POLL_MS = 100
const SEARCH_ON_MOUNT_MAX_ATTEMPTS = 50

/** Street lines use full (long) names — short_name abbreviates ("Downing St"). */
const longNameOf = (place: GoogleAPIPlace, type: string): string =>
  place.address_components.find(component => component.types.includes(type))?.long_name ?? ''

const AddressSearchInputLoaded: FC<Props & { apiKey: string }> = ({
  label,
  placeholder,
  value,
  onChange,
  required,
  isError,
  errorText,
  restrictToCountry,
  startIcon,
  onSelect,
  disabled,
  onBlur,
  onSubmitText,
  searchOnMount,
  apiKey,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTypedRef = useRef(false)

  const {
    placePredictions,
    getPlacePredictions,
    placesService,
    isPlacePredictionsLoading,
  } = usePlacesAutocompleteService({
    apiKey,
    debounce: 300,
    sessionToken: true,
    language: i18n.language?.split('-')[0] || 'en',
    options: {
      input: '',
      types: ['address'],
    },
  })

  const requestPredictions = useCallback((query: string) => {
    getPlacePredictions({
      input: query,
      ...(restrictToCountry && { componentRestrictions: { country: Array.isArray(restrictToCountry) ? restrictToCountry.map((code) => code.toLowerCase()) : restrictToCountry.toLowerCase() } }),
    })
    setIsDropdownOpen(true)
    setActiveIndex(-1)
  }, [getPlacePredictions, restrictToCountry])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e)
    hasTypedRef.current = true

    const query = e.target.value
    if (query.length > 0) requestPredictions(query)
    else setIsDropdownOpen(false)
  }, [onChange, requestPredictions])

  useEffect(() => {
    const query = searchOnMount ? value : ''
    if (!query) return

    const input = containerRef.current?.querySelector('input')
    input?.focus()
    input?.setSelectionRange(query.length, query.length)

    // The Maps script loads asynchronously and the service drops a request made before it is ready.
    let attempts = 0
    const timer = window.setInterval(() => {
      attempts += 1
      const isReady = typeof google !== 'undefined' && !!google.maps?.places
      if (!isReady && attempts < SEARCH_ON_MOUNT_MAX_ATTEMPTS) return
      window.clearInterval(timer)
      if (isReady && !hasTypedRef.current) requestPredictions(query)
    }, SEARCH_ON_MOUNT_POLL_MS)

    return () => window.clearInterval(timer)
    // Mount only: the initial value is what was carried over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePlaceSelect = useCallback((placeId: string) => {
    if (!placesService) return

    placesService.getDetails(
      { placeId, fields: ['address_components', 'formatted_address', 'geometry', 'place_id'] },
      (placeDetails, status) => {
        if (!placeDetails?.address_components) {
          // Places failures (quota, denied request) would otherwise be a click that does nothing —
          // close the dropdown so the user is not invited to re-click the same dead prediction.
          logger.error('Address search place details failed', undefined, {
            context: { component: 'AddressSearchInput', action: 'getDetailsFailed', metadata: { status: String(status) } },
          })
          setIsDropdownOpen(false)
          return
        }

        const googlePlace = { address_components: placeDetails.address_components } as GoogleAPIPlace
        // GB/IE addresses carry the city as postal_town instead of locality.
        const city = getCityFromGooglePlace(googlePlace) || longNameOf(googlePlace, 'postal_town')

        onSelect({
          streetAndNumber: [longNameOf(googlePlace, 'route'), longNameOf(googlePlace, 'street_number')]
            .filter(Boolean)
            .join(' '),
          postalCode: longNameOf(googlePlace, 'postal_code'),
          city,
          countryCode: getFromGooglePlace(googlePlace, GooglePlaceComponent.country),
        }, placeDetails)

        setIsDropdownOpen(false)
      }
    )
  }, [placesService, onSelect])

  // The loading row only shows once the service actually exists: when the Maps script never loads
  // (blocked/misconfigured), placesService stays null and an eternally-loading dropdown would show.
  const showDropdown = useMemo(() =>
    isDropdownOpen && (placePredictions.length > 0 || (isPlacePredictionsLoading && !!placesService)),
  [isDropdownOpen, placePredictions.length, isPlacePredictionsLoading, placesService])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!showDropdown) return
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((index) => (index + step + placePredictions.length) % placePredictions.length)
      return
    }
    if (e.key === 'Escape') {
      // Open suggestions take the first Escape; letting it reach an enclosing drawer closed the drawer too.
      if (showDropdown) e.stopPropagation()
      setIsDropdownOpen(false)
      return
    }
    if (e.key !== 'Enter') return
    const highlighted = showDropdown ? placePredictions[activeIndex] : undefined
    if (highlighted) {
      e.preventDefault()
      handlePlaceSelect(highlighted.place_id)
      return
    }
    if (!onSubmitText) return
    e.preventDefault()
    setIsDropdownOpen(false)
    onSubmitText(value)
  }, [showDropdown, placePredictions, activeIndex, handlePlaceSelect, onSubmitText, value])

  return (
    <ClickAwayListener onClickAway={() => setIsDropdownOpen(false)}>
      {/* fs-exclude: the typed value is a billing address — the input must redact itself even
          when a future consumer forgets to wrap it. */}
      <Box ref={containerRef} position="relative" className="fs-exclude">
        <MUIInputField
          label={label}
          value={value}
          placeholder={placeholder}
          required={required}
          isError={isError}
          errorText={errorText}
          startIcon={startIcon}
          disabled={disabled}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />

        {showDropdown &&
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 10,
              marginTop: '.4rem',
              borderRadius: '.8rem',
              overflow: 'hidden',
              maxHeight: '24rem',
              overflowY: 'auto',
            }}
          >
            {placePredictions.map((prediction, index) => (
              <MUIDropdownItem
                key={prediction.place_id}
                isSelected={index === activeIndex}
                onClick={() => handlePlaceSelect(prediction.place_id)}
                sx={{
                  padding: '1.2rem 2.4rem',
                  position: 'relative',
                  // Inset hairline between results, as in the Google address dropdown.
                  '& + &::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '2.4rem',
                    right: '2.4rem',
                    borderTop: `1px solid ${GRAY_BORDER}`,
                  },
                }}
              >
                <PredictionLabel prediction={prediction} />
              </MUIDropdownItem>
            ))}
          </Paper>
        }
      </Box>
    </ClickAwayListener>
  )
}
